/*
 * /assist/:token — entry to the help-desk assisted session on the shared
 * tablet (DESIGN-HELPDESK-KNOWN-TICKET-ASSISTED-COMPLETION-V1 §2.3; LOCKED
 * contract HELPDESK-CONTRACT-LOCKED-1102). PUBLIC (matched before the auth
 * gate in App.tsx); no Cognito session is used or created.
 *
 *   1. The single-use link token is taken out of the address bar at once and
 *      exchanged ONCE (POST /assist/exchange) for a 30-minute ticket-scoped
 *      session.
 *   2. enterAssistedMode (auth/authSource.ts) swaps web storage for memory and
 *      holds the session IN MEMORY ONLY; App then renders the assisted shell
 *      with the REAL Get Ready screens for that ticket (screens/AssistShell).
 *   3. A refused link shows plain copy (used / expired / invalid / not yet
 *      claimed).
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { enterAssistedMode } from '../auth/authSource'
import { Brand, Card } from '../components/ui'
import { AssistError, exchangeAssistToken } from '../data/assist'
import { myApiBase } from '../data/myApi'
import { AssistBanner } from './AssistShell'

const ERROR_COPY: Record<string, string> = {
  link_used: 'This help-desk link has already been opened. Please ask the help desk for a new one.',
  link_expired: 'This help-desk link has expired. Please ask the help desk for a new one.',
  link_not_found: 'This help-desk link is not valid. Please ask the help desk for a new one.',
  not_claimed: 'This ticket has not been claimed yet. Please finish signing in first, or ask the help desk to start again.',
  unconfigured: 'This page is not available right now. Please return the tablet to the help desk.',
  storage: 'This browser cannot open a help-desk session safely. Please return the tablet to the help desk.',
}
export const assistErrorCopy = (code: string) => ERROR_COPY[code] ?? 'Something went wrong. Please return the tablet to the help desk.'

export interface AssistLandingProps {
  token: string
  /** Test seams. */
  fetchImpl?: typeof fetch
  base?: string | null
}

export function AssistLanding({ token, fetchImpl = fetch, base = myApiBase() }: AssistLandingProps) {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    // Keep the single-use token out of the history stack.
    try {
      window.history.replaceState(null, '', '/assist')
    } catch {
      // non-browser test env
    }
    if (!base) {
      setError('unconfigured')
      return
    }
    void (async () => {
      try {
        const s = await exchangeAssistToken(base, token, fetchImpl)
        if (!enterAssistedMode(s)) {
          setError('storage')
          return
        }
        navigate('/get-ready', { replace: true })
      } catch (e) {
        setError(e instanceof AssistError ? e.code : 'unknown')
      }
    })()
  }, [base, token, fetchImpl, navigate])

  return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: 16 }} data-testid="assist-landing">
      <div style={{ marginBottom: 14 }}><Brand /></div>
      <AssistBanner />
      <Card>
        {error ? <p data-testid="assist-error">{assistErrorCopy(error)}</p> : <p>Opening your ticket…</p>}
      </Card>
    </div>
  )
}
